import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';
import { Col, Row, Statistic, Typography, Select, Table, Button, Spin } from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import { convertTimeFactor } from '../../../utils/functions';
import api from '../../../../api/api';
import envConfig from '../../../../envConfig';

function Locations() {
  const [loading, setLoading] = useState(false);
  const [map, setMap] = useState(null);
  const [userLocations, setUserLocations] = useState([]);
  const [timeFactor, setTimeFactor] = useState(0);
  const [top, setTop] = useState(1);
  const days = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 15,
    16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31
  ];
  const weeks = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 15,
    16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31,
    32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47,
    48, 49, 50, 51, 52
  ];
  const months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  const { Option, OptGroup } = Select;
  const [locations, setLocations] = useState(null);
  const [reload, setReload] = useState(false);
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: envConfig.REACT_APP_GEO_API_KEY,
    language: 'es-US',
    libraries: ['places']
  });
  const apiHasLoaded = (tempMap) => {
    const bounds = new window.google.maps.LatLngBounds({
      lat: 18.2178311,
      lng: -65.9660781
    });
    tempMap.fitBounds(bounds);
    setMap(tempMap);
  };
  const { t, i18n } = useTranslation();
  const onUnmount = React.useCallback((tempMap) => {
    setMap(null);
  }, []);

  function mapData(loc) {
    const mapped = _.map(loc, (p, index) => ({
      key: index,
      count: p.count,
      city: p.city
    }));
    return _.sortBy(mapped, (s) => s.startDate, 'desc');
  }

  function getData() {
    setLoading(true);
    api
      .get(`analytics/customers/${timeFactor}/${top}`)
      .then((response) => {
        setLoading(false);
        const mapped = mapData(response.data.data.userLocationsCreated[0].cities);
        setLocations(mapped);
        setUserLocations(response.data.data.allLocationsCreated);
      })
      .catch((error) => {
        console.error(error);
        setLoading(false);
      });
  }

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    getData();
  }, [timeFactor, top]);

  useEffect(() => {
    if (reload) {
      getData();
    }
  }, [reload]);

  return (
    <div className="p-4 border bg-gray-100 rounded-lg mb-4">
      <div className="flex flex-col" style={{ float: 'right' }}>
        <Button
          className="mb-4"
          size="small"
          type="primary"
          loading={loading}
          icon={<SyncOutlined spin={loading} />}
          onClick={() => {
            setReload(!reload);
          }}
        >
          Reload
        </Button>
      </div>
      <Row align="middle" gutter={[12, 12]} justify="space-between">
        <Col xs={24} sm={24} md={24} lg={10} xl={10}>
          <Typography.Title level={5}>{t('locations_analytics')}</Typography.Title>
          <Typography.Text type="secondary">
            {t('analytics_locations_description')}
          </Typography.Text>
        </Col>
        <Col xs={24} sm={24} md={24} lg={6} xl={6}>
          <div className="flex flex-row">
            {' '}
            <div className="mr-10">
              <Statistic title="Timespan" value={`${top} ${t(convertTimeFactor(timeFactor))}(s)`} />
            </div>
          </div>
        </Col>
        <Col xs={12} sm={12} md={12} lg={4} xl={4}>
          <div>
            <Select
              listHeight={500}
              disabled={loading}
              style={{ width: 150 }}
              onChange={(value) => setTimeFactor(value)}
              defaultValue={0}
            >
              <Option disabled>{t('timeFactor_selector_description')}</Option>
              <Option value={0}>{t('days')}</Option>
              <Option value={1}>{t('weeks')}</Option>
              <Option value={2}>{t('months')}</Option>
              <Option value={3} hidden>{t('years')}</Option>
            </Select>
          </div>
        </Col>
        <Col xs={12} sm={12} md={12} lg={4} xl={4}>
          {timeFactor === 0 ?
            <div>
              <Select
                listHeight={500}
                disabled={loading}
                style={{ width: 150 }}
                onChange={(value) => setTop(value)}
                defaultValue={top}
              >
                <OptGroup className="text-xs flex items-center">
                  {_.map(days, (num) => (
                    <Option
                      key={num}
                      value={num}
                      className="text-xs flex items-center"
                    >
                      {num}
                    </Option>
                  ))}
                </OptGroup>
              </Select>
            </div>
            : null}
          {timeFactor === 1 ?
            <div>
              <Select
                listHeight={500}
                disabled={loading}
                style={{ width: 150 }}
                onChange={(value) => setTop(value)}
                defaultValue={top}
              >
                <OptGroup className="text-xs flex items-center">
                  {_.map(weeks, (num) => (
                    <Option
                      key={num}
                      value={num}
                      className="text-xs flex items-center"
                    >
                      {num}
                    </Option>
                  ))}
                </OptGroup>
              </Select>
            </div>
            : null}
          {timeFactor === 2 ?
            <div>
              <Select
                listHeight={500}
                disabled={loading}
                style={{ width: 150 }}
                onChange={(value) => setTop(value)}
                defaultValue={top}
              >
                <OptGroup className="text-xs flex items-center">
                  {_.map(months, (num) => (
                    <Option
                      key={num}
                      value={num}
                      className="text-xs flex items-center"
                    >
                      {num}
                    </Option>
                  ))}
                </OptGroup>
              </Select>
            </div>
            : null}
        </Col>
        <Col>
          {' '}
        </Col>
      </Row>
      <br />
      <br />
      <Typography.Text>
        {userLocations && userLocations?.length === 1 ? `${userLocations?.length} Location` : `${userLocations?.length} Locations`}
      </Typography.Text>
      <Spin spinning={loading}>
        {isLoaded && (
          <GoogleMap
            mapContainerStyle={{
              height: '400px',
              width: '100%'
            }}
            zoom={20}
            onLoad={apiHasLoaded}
            onUnmount={onUnmount}
            center={{
              lat: 18.2178311,
              lng: -65.9660781
            }}
          >
            {_.map(userLocations, (loc) => (
              <Marker
                position={{ lat: loc.lat, lng: loc.lon }}
              />
            ))}
          </GoogleMap>
        )}
      </Spin>
      <br />
      <br />
      <Table
        size="small"
        loading={loading}
        bordered
        pagination={false}
        columns={[
          {
            title: t('city'),
            dataIndex: 'city',
            key: 'city',
            align: 'left',
            className: 'text-xs',
            render: (text) => <span>{text}</span>,
          },
          {
            title: t('count'),
            dataIndex: 'count',
            key: 'count',
            align: 'left',
            className: 'text-xs',
            render: (text) => <span>{text}</span>,
          },
        ]}
        dataSource={locations}
      />
    </div>
  );
}

export default Locations;
